import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

// [변경 사유]
// - 초기 기획 의도에 맞춰 "신호등"이 아닌 "기간별 체크리스트 + 상세 Top N" 형태로 표시합니다.
// - 백엔드가 아직 룰 엔진 결과(기간/임계치/근거)를 구조화해 내려주지 않으므로,
//   1차는 프론트에서 날짜 기준으로 3개월/1년/5년을 그룹핑하여 표시합니다.

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

// [변경 사유]
// - 요약 카드 영역과 "상세 리스트" 영역의 시각적 구분을 강화하기 위해 요약 영역을 섹션 박스로 감쌉니다.
const SummarySection = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #ffffff;
  padding: 10px;
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  /* [변경 사유]
   * - 카드 배경색을 크게 바꾸면 촌스럽고 위계가 흔들릴 수 있어,
   *   상단에 얇은 Risk 바(색상)만 추가하여 기간별 위험도를 직관적으로 구분합니다.
   */
  position: relative;
  overflow: hidden;
  border: 1px solid ${props => (props.active ? '#2b6cb0' : '#e2e8f0')};
  border-radius: 12px;
  padding: 12px;
  background: ${props => (props.active ? '#ebf8ff' : '#f7fafc')};
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  box-shadow: ${props => (props.active ? '0 0 0 1px #2b6cb0 inset' : 'none')};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => (props.riskColor ? props.riskColor : 'transparent')};
  }
`;

const SummaryTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 800;
  color: ${props => (props.active ? '#1a365d' : '#2c5282')};
`;

const SummaryDesc = styled.div`
  margin-top: 6px;
  font-size: 0.85rem;
  color: #4a5568;
`;

const Count = styled.span`
  font-weight: 800;
  color: #2b6cb0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Item = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid #edf2f7;
  border-radius: 12px;
  padding: 10px 12px;
  background: #fff;
`;

const Left = styled.div`
  min-width: 0;
`;

const ItemTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: #1a202c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemSub = styled.div`
  font-size: 0.8rem;
  color: #718096;
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Badge = styled.div`
  flex: 0 0 auto;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid ${props => props.border};
  background: ${props => props.bg};
  color: ${props => props.color};
  font-size: 0.75rem;
  font-weight: 800;
`;

const Empty = styled.div`
  color: #718096;
  font-size: 0.9rem;
`;

// [변경 사유]
// - disclosure_check_v2(기간별 요약/Top N) 스키마를 추가 지원합니다.
// - 기본 선택은 5년이며, 요약 카드 클릭 시 상세 리스트(Top N)를 해당 기간으로 필터링합니다.
// - v2가 없으면 기존(disclosure_check 배열) 렌더링을 유지합니다.

const _daysDiff = (dateStr) => {
  if (!dateStr) return Number.POSITIVE_INFINITY;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return Number.POSITIVE_INFINITY;
  const now = new Date();
  return Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
};

const _riskBadge = (item) => {
  const risk = item?.risk_level || (item?.status === 'critical' ? 'high' : item?.status === 'check' ? 'medium' : 'low');
  if (risk === 'high') {
    return { text: '필수 고지', bg: '#fff5f5', border: '#fed7d7', color: '#c53030' };
  }
  if (risk === 'medium') {
    return { text: '추가 확인', bg: '#fffff0', border: '#fefcbf', color: '#975a16' };
  }
  return { text: '참고', bg: '#f0fff4', border: '#c6f6d5', color: '#276749' };
};

// [변경 사유]
// - 기간별 요약 카드에 Risk 바를 표시하기 위해, 기간 내 아이템들의 최고 위험도를 계산합니다.
const _windowRiskLevel = (items) => {
  if (!Array.isArray(items) || items.length === 0) return 'low';
  const norm = (it) => {
    return it?.risk_level || (it?.status === 'critical' ? 'high' : it?.status === 'check' ? 'medium' : 'low');
  };
  if (items.some((it) => norm(it) === 'high')) return 'high';
  if (items.some((it) => norm(it) === 'medium')) return 'medium';
  return 'low';
};

const _riskBarColor = (level) => {
  if (level === 'high') return '#e53e3e'; // Critical(Red)
  if (level === 'medium') return '#d69e2e'; // Warning(Yellow)
  return '#38a169'; // Safe(Green)
};

const _groupByWindow = (items) => {
  const w3m = [];
  const w1y = [];
  const w5y = [];

  items.forEach((it) => {
    const diff = _daysDiff(it?.date);
    if (diff <= 90) w3m.push(it);
    else if (diff <= 365) w1y.push(it);
    else if (diff <= 365 * 5) w5y.push(it);
  });

  return { w3m, w1y, w5y };
};

const _topN = (items, n) => {
  const sorted = [...items].sort((a, b) => {
    const ra = a?.risk_level || (a?.status === 'critical' ? 'high' : a?.status === 'check' ? 'medium' : 'low');
    const rb = b?.risk_level || (b?.status === 'critical' ? 'high' : b?.status === 'check' ? 'medium' : 'low');
    const score = (r) => (r === 'high' ? 2 : r === 'medium' ? 1 : 0);
    return score(rb) - score(ra);
  });
  return sorted.slice(0, n);
};

const _isV2 = (data) => {
  return !!(data && typeof data === 'object' && !Array.isArray(data) && data.windows);
};

const PreCheckPanel = ({ data = [] }) => {
  const isV2 = _isV2(data);
  const defaultWindow = isV2 ? (data?.default_window || '5y') : '5y';
  const [selectedWindow, setSelectedWindow] = useState(defaultWindow);

  // [변경 사유]
  // - 상위에서 data가 비동기로 바뀌는 경우(새로고침/재조회)에도 기본 윈도우가 반영되도록 보강합니다.
  // - selectedWindow를 무조건 덮어쓰면 사용자 클릭이 무시될 수 있어, v2->v2처럼 유지되는 경우는 그대로 둡니다.
  useEffect(() => {
    if (!isV2) return;
    if (selectedWindow !== '3m' && selectedWindow !== '1y' && selectedWindow !== '5y') {
      setSelectedWindow(defaultWindow);
    }
  }, [isV2, defaultWindow, selectedWindow]);

  if (!data || (Array.isArray(data) && data.length === 0)) return <Empty>고지의무 점검 대상 이력이 없습니다.</Empty>;

  if (isV2) {
    const windows = data?.windows || {};
    const w3m = windows?.['3m'] || { summary: {}, items_top_n: [] };
    const w1y = windows?.['1y'] || { summary: {}, items_top_n: [] };
    const w5y = windows?.['5y'] || { summary: {}, items_top_n: [] };

    const risk3m = _riskBarColor(_windowRiskLevel(w3m?.items_top_n));
    const risk1y = _riskBarColor(_windowRiskLevel(w1y?.items_top_n));
    const risk5y = _riskBarColor(_windowRiskLevel(w5y?.items_top_n));

    const selected = windows?.[selectedWindow] || w5y;
    const items = Array.isArray(selected?.items_top_n) ? selected.items_top_n : [];

    const _summaryText = (windowKey, w) => {
      const s = w?.summary || {};
      const flags = Number(s?.checkup_flags || 0);
      const disp = Number(s?.medication_dispense_count || 0);

      if (windowKey === '5y') {
        const epi = Number(s?.medication_episode_30d || 0);
        return `검진 ${flags}건 · 연속투약(30일↑) ${epi}건 · 투약 ${disp}건`;
      }

      const disp7 = Number(s?.medication_dispense_7d_or_more || 0);
      const disp30 = Number(s?.medication_dispense_30d_or_more || 0);
      return `검진 ${flags}건 · 투약(7일↑) ${disp7}건 · 투약(30일↑) ${disp30}건`;
    };

    return (
      <Container>
        <SummarySection>
          <SummaryRow>
            <SummaryCard clickable active={selectedWindow === '3m'} riskColor={risk3m} onClick={() => setSelectedWindow('3m')}>
              <SummaryTitle active={selectedWindow === '3m'}>최근 3개월 체크</SummaryTitle>
              <SummaryDesc>
                {_summaryText('3m', w3m)}
              </SummaryDesc>
            </SummaryCard>
            <SummaryCard clickable active={selectedWindow === '1y'} riskColor={risk1y} onClick={() => setSelectedWindow('1y')}>
              <SummaryTitle active={selectedWindow === '1y'}>최근 1년 체크</SummaryTitle>
              <SummaryDesc>
                {_summaryText('1y', w1y)}
              </SummaryDesc>
            </SummaryCard>
            <SummaryCard clickable active={selectedWindow === '5y'} riskColor={risk5y} onClick={() => setSelectedWindow('5y')}>
              <SummaryTitle active={selectedWindow === '5y'}>최근 5년 체크</SummaryTitle>
              <SummaryDesc>
                {_summaryText('5y', w5y)}
              </SummaryDesc>
            </SummaryCard>
          </SummaryRow>
        </SummarySection>

        <div>
          <SummaryTitle style={{ marginBottom: '8px' }}>상세 리스트 (Top 10)</SummaryTitle>
          <List>
            {items.length === 0 ? (
              <Empty>해당 기간에 표시할 점검 내역이 없습니다.</Empty>
            ) : (
              items.map((item, idx) => {
                const badge = _riskBadge(item);
                return (
                  <Item key={idx}>
                    <Left>
                      <ItemTitle>{item?.title || item?.detail || '항목'}</ItemTitle>
                      <ItemSub>{`${item?.date || ''} · ${item?.category || ''}`}</ItemSub>
                    </Left>
                    <Badge bg={badge.bg} border={badge.border} color={badge.color}>
                      {badge.text}
                    </Badge>
                  </Item>
                );
              })
            )}
          </List>
        </div>
      </Container>
    );
  }

  // 기존 disclosure_check(배열) 렌더링
  if (!data || data.length === 0) return <Empty>고지의무 점검 대상 이력이 없습니다.</Empty>;

  const { w3m, w1y, w5y } = _groupByWindow(data);

  const risk3m = _riskBarColor(_windowRiskLevel(w3m));
  const risk1y = _riskBarColor(_windowRiskLevel(w1y));
  const risk5y = _riskBarColor(_windowRiskLevel(w5y));

  const top3m = _topN(w3m, 5);
  const top1y = _topN(w1y, 5);
  const top5y = _topN(w5y, 5);

  return (
    <Container>
      <SummarySection>
        <SummaryRow>
          <SummaryCard riskColor={risk3m}>
            <SummaryTitle>최근 3개월 체크</SummaryTitle>
            <SummaryDesc>
              해당 이력 <Count>{w3m.length}</Count>건
            </SummaryDesc>
          </SummaryCard>
          <SummaryCard riskColor={risk1y}>
            <SummaryTitle>최근 1년 체크</SummaryTitle>
            <SummaryDesc>
              해당 이력 <Count>{w1y.length}</Count>건
            </SummaryDesc>
          </SummaryCard>
          <SummaryCard riskColor={risk5y}>
            <SummaryTitle>최근 5년 체크</SummaryTitle>
            <SummaryDesc>
              해당 이력 <Count>{w5y.length}</Count>건
            </SummaryDesc>
          </SummaryCard>
        </SummaryRow>
      </SummarySection>

      <div>
        <SummaryTitle style={{ marginBottom: '8px' }}>상세 리스트 (Top 10)</SummaryTitle>
        <List>
          {_topN([...top3m, ...top1y, ...top5y], 10).map((item, idx) => {
            const badge = _riskBadge(item);
            return (
              <Item key={idx}>
                <Left>
                  <ItemTitle>{item?.detail || '항목'}</ItemTitle>
                  <ItemSub>{`${item?.date || ''} · ${item?.category || ''}`}</ItemSub>
                </Left>
                <Badge bg={badge.bg} border={badge.border} color={badge.color}>
                  {badge.text}
                </Badge>
              </Item>
            );
          })}
        </List>
      </div>
    </Container>
  );
};

export default PreCheckPanel;
